// filepath: apps/web/src/lib/useDirtyTrackedFields.ts
//
// The web design-adherence scanner matches /#[0-9a-fA-F]{3,8}\b/ anywhere in a
// source file, and a GitLab issue reference is hex digits behind a hash. This
// file has no UI and emits no styles; the only two hits are issue numbers. The
// scanner drops a line only when the marker is ON that line, so the marker sits
// on each affected line below rather than in a note about them — a note
// containing the issue number is itself flagged, which is how the first attempt
// at this fix made the count worse instead of better.
// design-adherence-ignore — the issue number here is not a colour.
// design-adherence-ignore — #805 — per-field dirty tracking for values that arrive asynchronously.
//
// THE DEFECT THIS REPLACES
//
// Three admin forms re-seeded their state from an `initial` object inside a
// `useEffect` whose deps were the incoming values:
//
//   OrganizationUnitForm.tsx:136  [initial.name, initial.code,
//                                  initial.legal_entity_id, initial.parent_...]
//   LocationForm.tsx:83          [initial.parent_location_id, initial.name, initial.code]
//   PersonForm.tsx:63            [initial.display_name, initial.email,
//                                  initial.phone, initial.status]
//
// Each resets EVERY field when ANY ONE dep changes — so a field the user is
// editing is discarded because a *sibling* changed. In `LocationForm`,
// `parent_location_id` is a sibling of the user-editable `name`/`code`, so
// moving a location's parent in another session wipes the typed name.
//
// Normalising the values (`?? null`) would close one instance and leave the
// class intact: any sibling added tomorrow reproduces it. The coupling is the
// defect, so this hook removes the coupling — a field re-seeds only when
// THAT field's own server value changes, and never after the user has edited it.
//
// design-adherence-ignore — BA RULING (note_23051 on #805, `prio::0`, "Slice 1 - directory ready")
//
//   1. A field is seeded once, when the record first loads or the route points
//      at a different record.
//   2. After that a field re-seeds ONLY while the user has not edited it.
//   3. No field's re-seed may depend on a sibling's value or representation.
//   4. If the server value changed under an EDITED field, keep the user's text
//      and surface the current server value in a non-blocking notice.
//   5. On a row_version conflict the edit survives; it reaches the server with
//      the new row_version only because the user was told and re-submitted.
//
// No build tag: this is production code, not a test.
import * as React from "react";

/** One field's server value and whether the user has touched it. */
export interface DirtyTrackedField<T> {
  /** Current value: the user's if edited, otherwise the server's. */
  value: T;
  /** Setter to hand to the input's onChange. Marks the field dirty. */
  set: (next: T) => void;
  /** True once the user has edited this field. */
  isDirty: boolean;
  /**
   * The server's value when it changed underneath an EDITED field, else
   * undefined. Drives the non-blocking notice (rule 4).
   */
  serverConflictValue: T | undefined;
  /** Dismiss the notice for this field. */
  acknowledgeServerConflict: () => void;
}

/**
 * useDirtyTrackedFields returns one DirtyTrackedField per key in `serverValues`.
 *
 * `serverValues` is a plain object of the CURRENT server values for the fields
 * this form owns. Each key is tracked independently: its re-seed depends on its
 * own value only (rule 3), and not at all once `isDirty` (rule 2).
 *
 * The whole object resets only when `recordKey` changes — the record identity,
 * i.e. "the route points at a different record" (rule 1). Pass a primitive
 * (a uuid, or `${id}:${mode}`); passing the object itself would re-trigger on
 * every render.
 *
 * @param serverValues current server values, keyed by field name.
 * @param recordKey    identity of the record being edited. A change here
 *                     re-seeds every field and clears dirty state.
 */
export function useDirtyTrackedFields<T extends Record<string, unknown>>(
  serverValues: T,
  recordKey: string,
): { [K in keyof T]: DirtyTrackedField<T[K]> } {
  type State = {
    dirty: Record<string, boolean>;
    overrides: Record<string, unknown>;
    conflicts: Record<string, unknown>;
  };
  // Internally everything is keyed by STRING. `Object.keys` only ever yields
  // strings, so that is the real runtime key type; `keyof T` additionally
  // admits number|symbol, which would force every index access below through a
  // cast anyway. The cast happens once, at this boundary.
  type K = Extract<keyof T, string>;
  const keys = Object.keys(serverValues) as K[];

  const [state, setState] = React.useState<State>({
    dirty: {},
    overrides: {},
    conflicts: {},
  });
  const prevServer = React.useRef<Record<string, unknown>>({});
  const prevRecordKey = React.useRef<string | null>(null);

  // Rule 3: compare each field against ITS OWN previous server value only.
  // A sibling's change cannot reach into this field.
  React.useEffect(() => {
    const prev = prevServer.current;
    const next: State = {
      dirty: state.dirty,
      overrides: state.overrides,
      conflicts: state.conflicts,
    };
    let changed = false;

    for (const key of keys) {
      const before = prev[key];
      const after = serverValues[key];
      const isRecordSwitch = prevRecordKey.current !== recordKey;
      const isFirstLoad = !(key in prev);

      if (isFirstLoad || isRecordSwitch) {
        // Rule 1: seed on first load or when the route points elsewhere.
        if (!isFirstLoad) {
          next.dirty = { ...next.dirty, [key]: false };
          next.overrides = omit(next.overrides, key);
          next.conflicts = omit(next.conflicts, key);
          changed = true;
        }
        continue;
      }

      if (Object.is(before, after)) {
        continue; // this field's own server value did not change
      }

      if (next.dirty[key]) {
        // Rule 4: user has edited it. Keep their text; record the server value
        // so the form can show a non-blocking notice.
        next.conflicts = { ...next.conflicts, [key]: after };
        changed = true;
        continue;
      }

      // Rule 2: not edited, so this field re-seeds from the server.
      next.overrides = omit(next.overrides, key);
      next.conflicts = omit(next.conflicts, key);
      changed = true;
    }

    if (changed) {
      setState(next);
    }

    prevServer.current = serverValues as Record<string, unknown>;
    prevRecordKey.current = recordKey;
    // `state` is intentionally not a dep: this effect derives from the server
    // values and recordKey only. Reading `state` inside would re-run it on
    // every keystroke and re-seed fields the user is typing into.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverValues, recordKey]);

  const build = React.useCallback(
    <F extends K>(key: F): DirtyTrackedField<T[F]> => {
      const isDirty = state.dirty[key] === true;
      const serverValue = serverValues[key];
      return {
        value: (isDirty ? (state.overrides[key] as T[F]) : serverValue) as T[F],
        set: (next: T[F]) => {
          setState((prev) => ({
            dirty: { ...prev.dirty, [key]: true },
            overrides: { ...prev.overrides, [key]: next },
            conflicts: omit(prev.conflicts, key),
          }));
        },
        isDirty,
        serverConflictValue: state.conflicts[key] as T[F] | undefined,
        acknowledgeServerConflict: () => {
          setState((prev) => ({
            ...prev,
            conflicts: omit(prev.conflicts, key),
          }));
        },
      };
    },
    [state, serverValues],
  );

  const out = {} as { [P in keyof T]: DirtyTrackedField<T[P]> };
  for (const key of keys) {
    out[key] = build(key);
  }
  return out;
}

function omit(
  src: Record<string, unknown>,
  key: string,
): Record<string, unknown> {
  if (!(key in src)) {
    return src;
  }
  const next = { ...src };
  delete next[key];
  return next;
}
